import React, { useCallback, useEffect, useState } from 'react';
import { Grid, Paper, Typography, FormControl, InputLabel, Select, MenuItem, FormControlLabel, Checkbox } from '@mui/material';
import type { GridRowSelectionModel } from '@mui/x-data-grid';
import { useNavigate } from 'react-router';
import { Box } from '@mui/system';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import Button from '@mui/material/Button';
import b64toBlob from 'b64-to-blob';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';
import { RootState } from '../../../reducers';
import Authorization from '../../../utils/Authorization';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useWidgetContext } from '../../../hooks/widgetContext';
import { fetchDocumentationTrackingSummary, MenuProps, exportDocumentationTrackingSummary } from '../WidgetService';
import { overrideAppointmentDocumentationStatus } from '../../../store/appointment.slice';
import DocumentationTrackingSummary from '../../../Models/Widget/DocumentationTrackingSummary.model';
import { TimePeriodFilterEnum } from '../../../Enum/TimePeriodFilterEnum';
import { parentUrl } from '../../../utils/constants';
import { getDateFilter } from '../../HedisMetrics/HedisMetricService';
import { dateFormatter } from '../../../utils/timeFormat';
import TeamDropdown from '../../Shared/TeamDropdown';
import { fetchTeams } from '../../../store/careTeams.slice';
import '../Widgets.css';

const DocumentationTrackingSummaryDetail = () => {
    const authData = new Authorization();
    const dispatch = useAppDispatch();
    const history = useNavigate();
    const { careTeams: teams } = useSelector((state: RootState) => state.careTeams);
    const { activeProviders } = useSelector((state: RootState) => state.provider);
    const [isMembersLoading, setIsMembersLoading] = useState<boolean>(false);
    const [isFetched, setIsFetched] = useState<boolean>(false);
    const {
        appointmentSelectedProvider,
        setAppointmentSelectedProvider,
        appointmentSelectedTeam,
        setAppointmentSelectedTeam,
        appointmentDateFilter,
        setAppointmentDateFilter,
        appointmentFromDate,
        setAppointmentFromDate,
        appointmentToDate,
        setAppointmentToDate,
        appointmentShowInActiveProviders,
        setAppointmentShowInActiveProviders,
        appointmentShowInActiveTeams,
        setAppointmentShowInActiveTeams,
    } = useWidgetContext();
    const [members, setMembers] = useState<DocumentationTrackingSummary>();
    const [downLoading, setDownLoading] = useState<boolean>(false);
    const [loadedPage, setLoadedPage] = useState(false);
    const hasPermission = authData.isInPermission('Adjust Not Documented Appointments');
    const filteredProviders = (appointmentShowInActiveProviders ? activeProviders : activeProviders.filter((p) => p.Role.RoleName !== 'Inactive')) || [];

    const [notDocumentedSelectionModel, setNotDocumentedSelectionModel] = useState<GridRowSelectionModel | undefined>(undefined);
    const [notDocumentedSelectedRows, setNotDocumentedSelectedRows] = useState<any[]>([]);
    const [within3DaysDocumentedSelectionModel, setWithin3DaysDocumentedSelectionModel] = useState<GridRowSelectionModel | undefined>(undefined);
    const [within3DaysDocumentedSelectedRows, setWithin3DaysDocumentedSelectedRows] = useState<any[]>([]);
    const [documentedSelectionModel, setDocumentedSelectionModel] = useState<GridRowSelectionModel | undefined>(undefined);
    const [documentedSelectedRows, setDocumentedSelectedRows] = useState<any[]>([]);
    const [notDocumentingWithin3Days, setNotDocumentingWithin3Days] = useState(false);
    const [documenting, setDocumenting] = useState(false);
    const [notDocumenting, setNotDocumenting] = useState(false);

    useEffect(() => {
        dispatch(fetchTeams(true));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filterList = getDateFilter().filter((d) => d.Value !== 'Custom' && d.Value !== 'Current Month');

    const initialPage = useCallback(async () => {
        if (isFetched) {
            return;
        }
        setIsFetched(true);
    }, [isFetched]);

    useEffect(() => {
        initialPage();
    }, [initialPage]);

    useEffect(() => {
        if (activeProviders?.length && teams?.length) {
            setLoadedPage(true);
        }
    }, [teams, activeProviders]);

    const changeFilter = async () => {
        setIsMembersLoading(false);
        const data = await fetchDocumentationTrackingSummary(
            appointmentSelectedProvider,
            appointmentSelectedTeam,
            appointmentDateFilter,
            Boolean(appointmentFromDate) && appointmentDateFilter === TimePeriodFilterEnum.Custom ? new Date(appointmentFromDate).toISOString() : '',
            Boolean(appointmentToDate) && appointmentDateFilter === TimePeriodFilterEnum.Custom ? new Date(appointmentToDate).toISOString() : '',
            appointmentShowInActiveTeams,
            appointmentShowInActiveProviders,
            true
        );
        setMembers(data);
        setIsMembersLoading(true);
        setIsFetched(true);
    };

    useEffect(() => {
        if (loadedPage && (appointmentDateFilter !== TimePeriodFilterEnum.Custom || (Boolean(appointmentFromDate) && Boolean(appointmentToDate)))) {
            changeFilter();
        }

        if (appointmentDateFilter !== TimePeriodFilterEnum.Custom && Boolean(appointmentFromDate) && Boolean(appointmentToDate)) {
            setAppointmentFromDate(null);
            setAppointmentToDate(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        loadedPage,
        appointmentSelectedTeam,
        appointmentDateFilter,
        appointmentFromDate,
        appointmentToDate,
        setAppointmentFromDate,
        setAppointmentToDate,
        setIsFetched,
        appointmentSelectedProvider,
        appointmentShowInActiveTeams,
        appointmentShowInActiveProviders,
    ]);

    const backToWidget = () => {
        history('/provider/metricsummary/smart-dashboard');
    };

    const changeFromDate = (date: any) => {
        setAppointmentFromDate(date);
        let dateInfo = new Date(date);
        if (!Boolean(appointmentToDate) || new Date(appointmentToDate) <= dateInfo) {
            let endDate = dateInfo.setDate(dateInfo.getDate() + 30);
            setAppointmentToDate(endDate);
        }
    };

    const changeToDate = (date: any) => {
        setAppointmentToDate(date);
        let dateInfo = new Date(date);
        if (!Boolean(appointmentFromDate) || new Date(appointmentFromDate) >= dateInfo) {
            let startDate = dateInfo.setDate(dateInfo.getDate() - 30);
            setAppointmentFromDate(startDate);
        }
    };

    const exportDetail = async () => {
        setDownLoading(true);
        const data = await exportDocumentationTrackingSummary(
            appointmentSelectedProvider,
            appointmentSelectedTeam,
            appointmentDateFilter,
            Boolean(appointmentFromDate) && appointmentDateFilter === TimePeriodFilterEnum.Custom ? new Date(appointmentFromDate).toISOString() : '',
            Boolean(appointmentToDate) && appointmentDateFilter === TimePeriodFilterEnum.Custom ? new Date(appointmentToDate).toISOString() : '',
            appointmentShowInActiveTeams,
            appointmentShowInActiveProviders
        );

        let blob = b64toBlob(data.base64String, 'text/csv');
        let url = window.URL.createObjectURL(blob);
        let link = document.createElement('a');
        link.href = url;
        var date = new Date();
        var mm = date.getMonth() + 1; // getMonth() is zero-based
        var dd = date.getDate();
        var dateInfo = [(mm > 9 ? '' : '0') + mm, (dd > 9 ? '' : '0') + dd, date.getFullYear()].join('');
        link.setAttribute('download', `DocumentationTracking - ${dateInfo}.xlsx`);
        link.click();
        link.remove();

        setDownLoading(false);
    };

    const toggleProviderHandler = (isChecked: boolean) => {
        setAppointmentShowInActiveProviders(isChecked);
        setAppointmentSelectedProvider('All');
    };

    const columns: GridColDef[] = [
        {
            field: 'Name',
            headerName: 'Member Name',
            flex: 1,
        },
        {
            field: 'DateInfo',
            headerName: 'Appointment Date',
            width: 150,
            valueGetter: (value, row) => `${Boolean(row.DateInfo) ? dateFormatter(new Date(row.DateInfo)) : '-'}`,
        },
        {
            field: 'VisitedDate',
            headerName: 'Documented Date',
            width: 150,
            valueGetter: (value, row) => `${Boolean(row.VisitedDate) ? dateFormatter(new Date(row.VisitedDate)) : '-'}`,
        },
    ];

    const updateAppointmentDocumentation = async (type: number) => {
        let isDocument = false;
        let selectedIds = [];
        switch (type) {
            case 1:
                setNotDocumentingWithin3Days(true);
                isDocument = false;
                selectedIds = within3DaysDocumentedSelectedRows.filter((c) => c.ItemId != null).map((c) => c.ItemId);
                break;
            case 2:
                setNotDocumenting(true);
                isDocument = false;
                selectedIds = documentedSelectedRows.filter((c) => c.ItemId != null).map((c) => c.ItemId);
                break;
            case 3:
                setDocumenting(true);
                isDocument = true;
                selectedIds = notDocumentedSelectedRows.filter((c) => c.ItemId != null).map((c) => c.ItemId);
                break;
        }

        dispatch(
            overrideAppointmentDocumentationStatus(selectedIds, isDocument, (data) => {
                changeFilter();
                setNotDocumentedSelectionModel(undefined);
                setNotDocumentedSelectedRows([]);
                setDocumentedSelectionModel(undefined);
                setDocumentedSelectedRows([]);
                setWithin3DaysDocumentedSelectionModel(undefined);
                setWithin3DaysDocumentedSelectedRows([]);
                setDocumenting(false);
                setNotDocumenting(false);
                setNotDocumentingWithin3Days(false);
            })
        );
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Paper elevation={5}>
                <Grid container spacing={2} paddingLeft={2} paddingRight={2} paddingTop={1}>
                    <Grid size={12} paddingBottom={1}>
                        <span onClick={backToWidget}>
                            <ArrowBackIcon className="pointer mr-2" style={{ color: 'black' }} titleAccess="Back" />
                        </span>
                        <Typography display={'inline-block'} variant="h6" color={'primary'}>
                            Documentation Tracking Member Summary
                        </Typography>
                    </Grid>
                </Grid>
                <Grid container pl={2} mt={0} spacing={2}>
                    <Grid size={2}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Providers</InputLabel>
                            <Select
                                value={appointmentSelectedProvider}
                                label="Providers"
                                name="Providers"
                                onChange={(e) => setAppointmentSelectedProvider(e.target.value)}
                                MenuProps={MenuProps}
                            >
                                <MenuItem key="All" value="All">
                                    All
                                </MenuItem>
                                <FormControlLabel
                                    sx={{ pl: '12px', mb: '0px' }}
                                    control={<Checkbox checked={appointmentShowInActiveProviders} onChange={(e) => toggleProviderHandler(e.target.checked)} />}
                                    label="Show Inactive"
                                />
                                {filteredProviders.map((pro) => (
                                    <MenuItem key={pro.Id} value={pro.Id} sx={{ color: pro.Role.RoleName === 'Inactive' ? 'grey' : 'black' }}>
                                        {pro.LastName}, {pro.FirstName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={2}>
                        <TeamDropdown
                            teams={teams}
                            selectedTeam={appointmentSelectedTeam}
                            onChange={(id) => setAppointmentSelectedTeam(id)}
                            showInactive={appointmentShowInActiveTeams}
                            onToggleInactive={setAppointmentShowInActiveTeams}
                        />
                    </Grid>
                    <Grid size={2}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Date Filter</InputLabel>
                            <Select
                                value={appointmentDateFilter}
                                label="Date Filter"
                                name="Date"
                                onChange={(e) => setAppointmentDateFilter(parseInt(e.target.value))}
                            >
                                {filterList.map((date) => (
                                    <MenuItem key={date.Id} value={date.Id}>
                                        {date.Value}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    {parseInt(appointmentDateFilter) === TimePeriodFilterEnum.Custom ? (
                        <React.Fragment>
                            <Grid size={2}>
                                <DesktopDatePicker
                                    label="From Date"
                                    value={Boolean(appointmentFromDate) ? appointmentFromDate : null}
                                    minDate={new Date('2017-01-01')}
                                    onChange={(newValue) => changeFromDate(newValue)}
                                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                />
                            </Grid>
                            <Grid size={2}>
                                <DesktopDatePicker
                                    label="To Date"
                                    value={Boolean(appointmentToDate) ? appointmentToDate : null}
                                    minDate={new Date('2017-01-01')}
                                    onChange={(newValue) => changeToDate(newValue)}
                                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                />
                            </Grid>
                        </React.Fragment>
                    ) : null}
                    <Grid size={parseInt(appointmentDateFilter) === TimePeriodFilterEnum.Custom ? 1 : 4.8}></Grid>
                    <Grid size={1}>
                        <Button
                            style={{ minWidth: '120px', marginLeft: 'auto' }}
                            onClick={() => exportDetail()}
                            variant="contained"
                            color="success"
                            disabled={downLoading || (members?.DocumentedWithin3DaysAppointmentsMembers?.length === 0 && members?.NotDocumentedAppointmentsMembers?.length === 0)}
                        >
                            {downLoading ? 'Exporting...' : 'Export'}
                        </Button>
                    </Grid>
                </Grid>
                <hr />
                <Grid container spacing={2} paddingLeft={2} paddingRight={2} paddingBottom={2} paddingTop={2}>
                    <Grid size={12} minHeight="60vh" sx={{ position: 'relative' }}>
                        {!isMembersLoading ? (
                            <Box className="loader-center">
                                <CircularProgress />
                            </Box>
                        ) : (
                            <Grid container spacing={2}>
                                <Grid size={4}>
                                    <Grid container>
                                        <Grid size={5.5} display={'flex'} mb={2} maxHeight="55px" justifyContent={'flex-start'}>
                                            {hasPermission ? (
                                                <Button
                                                    style={{ minWidth: '150px', fontSize: '12px' }}
                                                    onClick={() => updateAppointmentDocumentation(1)}
                                                    variant="contained"
                                                    color="primary"
                                                    disabled={
                                                        notDocumentingWithin3Days ||
                                                        !Boolean(members?.DocumentedWithin3DaysAppointmentsMembers?.length) ||
                                                        within3DaysDocumentedSelectedRows.length === 0
                                                    }
                                                >
                                                    {notDocumentingWithin3Days ? 'Documenting..' : 'Mark As Not Documented'}
                                                </Button>
                                            ) : null}
                                        </Grid>
                                        <Grid size={6.5}>
                                            <Typography display={'inline-block'} sx={{ fontSize: '16px', marginTop: 1, float: 'right' }} color={'primary'}>
                                                Documented Within 3 Days ({members.DocumentedWithin3DaysAppointmentsMembers.length})
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                    <Grid size={12} style={{ height: '67vh', width: '100%' }}>
                                        <DataGrid
                                            onCellClick={(params: any, event: React.MouseEvent) => {
                                                // Prevent checkbox click from triggering cell click action
                                                if (params.field === '__check__') {
                                                    event.stopPropagation(); // Prevent default row click behavior
                                                    return;
                                                }
                                                window.open(`${parentUrl}provider/members-list/member-chart/${params.row.Id}/scheduling`, '_blank');
                                            }}
                                            columns={columns}
                                            rows={members.DocumentedWithin3DaysAppointmentsMembers}
                                            checkboxSelection={hasPermission}
                                            rowSelectionModel={within3DaysDocumentedSelectionModel}
                                            onRowSelectionModelChange={(newSelection: GridRowSelectionModel) => {
                                                let selectionArray: string[] = [];

                                                if (Array.isArray(newSelection)) {
                                                    selectionArray = newSelection.map(id => String(id));
                                                } else if (newSelection?.ids instanceof Set) {
                                                    selectionArray = Array.from(newSelection.ids).map(id => String(id));
                                                }

                                                setWithin3DaysDocumentedSelectionModel(newSelection);
                                                const selected = members.DocumentedWithin3DaysAppointmentsMembers?.filter(row =>
                                                    selectionArray.includes(`${row.ItemId}-${row.DateInfo}`)
                                                );
                                                setWithin3DaysDocumentedSelectedRows(selected || []);
                                            }}
                                            isRowSelectable={(params) => {
                                                return params.row?.OverrideDocumentationStatus === true;
                                            }}
                                            getRowId={(row) => `${row.ItemId}-${row.DateInfo}`}
                                            paginationMode="server"
                                            hideFooterPagination={true}
                                            hideFooter={true}
                                        />
                                    </Grid>
                                </Grid>
                                <Grid size={4}>
                                    <Grid container>
                                        <Grid size={5.5} display={'flex'} mb={2} maxHeight="55px" justifyContent={'flex-start'}>
                                            {hasPermission ? (
                                                <Button
                                                    style={{ minWidth: '150px', fontSize: '12px' }}
                                                    onClick={() => updateAppointmentDocumentation(2)}
                                                    variant="contained"
                                                    color="primary"
                                                    disabled={
                                                        notDocumenting ||
                                                        !Boolean(members?.DocumentedAppointmentsMembers?.length) ||
                                                        documentedSelectedRows.length === 0
                                                    }
                                                >
                                                    {notDocumenting ? 'Documenting..' : 'Mark As Not Documented'}
                                                </Button>
                                            ) : null}
                                        </Grid>
                                        <Grid size={6.5}>
                                            <Typography display={'inline-block'} sx={{ fontSize: '16px', marginTop: 1, float: 'right' }} color={'primary'}>
                                                Documented ({members.DocumentedAppointmentsMembers.length})
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                    <Grid size={12} style={{ height: '67vh', width: '100%' }}>
                                        <DataGrid
                                            onCellClick={(params: any, event: React.MouseEvent) => {
                                                // Prevent checkbox click from triggering cell click action
                                                if (params.field === '__check__') {
                                                    event.stopPropagation(); // Prevent default row click behavior
                                                    return;
                                                }
                                                window.open(`${parentUrl}provider/members-list/member-chart/${params.row.Id}/scheduling`, '_blank');
                                            }}
                                            columns={columns}
                                            rows={members.DocumentedAppointmentsMembers}
                                            checkboxSelection={hasPermission}
                                            rowSelectionModel={documentedSelectionModel}
                                            onRowSelectionModelChange={(newSelection: GridRowSelectionModel) => {
                                                let selectionArray: string[] = [];

                                                if (Array.isArray(newSelection)) {
                                                    selectionArray = newSelection.map(id => String(id));
                                                } else if (newSelection?.ids instanceof Set) {
                                                    selectionArray = Array.from(newSelection.ids).map(id => String(id));
                                                }

                                                setDocumentedSelectionModel(newSelection);
                                                const selected = members.DocumentedAppointmentsMembers?.filter(row =>
                                                    selectionArray.includes(`${row.ItemId}-${row.DateInfo}`)
                                                );
                                                setDocumentedSelectedRows(selected || []);
                                            }}
                                            isRowSelectable={(params) => {
                                                return params.row?.OverrideDocumentationStatus === true;
                                            }}
                                            getRowId={(row) => `${row.ItemId}-${row.DateInfo}`}
                                            paginationMode="server"
                                            hideFooterPagination={true}
                                            hideFooter={true}
                                        />
                                    </Grid>
                                </Grid>
                                <Grid size={4}>
                                    <Grid container>
                                        <Grid size={5.5} display={'flex'} mb={2} maxHeight="55px" justifyContent={'flex-start'}>
                                            {hasPermission ? (
                                                <Button
                                                    style={{ minWidth: '150px', fontSize: '12px' }}
                                                    onClick={() => updateAppointmentDocumentation(3)}
                                                    variant="contained"
                                                    color="primary"
                                                    disabled={
                                                        documenting ||
                                                        !Boolean(members?.NotDocumentedAppointmentsMembers?.length) ||
                                                        notDocumentedSelectedRows.length === 0
                                                    }
                                                >
                                                    {documenting ? 'Documenting..' : 'Mark As Documented'}
                                                </Button>
                                            ) : null}
                                        </Grid>
                                        <Grid size={6.5}>
                                            <Typography display={'inline-block'} sx={{ fontSize: '16px', marginTop: 1, float: 'right' }} color={'primary'}>
                                                Not Documented ({members.NotDocumentedAppointmentsMembers.length})
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                    <Grid size={12} style={{ height: '67vh', width: '100%' }}>
                                        <DataGrid
                                            onCellClick={(params: any, event: React.MouseEvent) => {
                                                // Prevent checkbox click from triggering cell click action
                                                if (params.field === '__check__') {
                                                    event.stopPropagation(); // Prevent default row click behavior
                                                    return;
                                                }
                                                window.open(`${parentUrl}provider/members-list/member-chart/${params.row.Id}/scheduling`, '_blank');
                                            }}
                                            columns={columns}
                                            rows={members.NotDocumentedAppointmentsMembers}
                                            checkboxSelection={hasPermission}
                                            rowSelectionModel={notDocumentedSelectionModel}
                                            onRowSelectionModelChange={(newSelection: GridRowSelectionModel) => {
                                                let selectionArray: string[] = [];

                                                if (Array.isArray(newSelection)) {
                                                    selectionArray = newSelection.map(id => String(id));
                                                } else if (newSelection?.ids instanceof Set) {
                                                    selectionArray = Array.from(newSelection.ids).map(id => String(id));
                                                }

                                                setNotDocumentedSelectionModel(newSelection);
                                                const selected = members.NotDocumentedAppointmentsMembers?.filter(row =>
                                                    selectionArray.includes(`${row.ItemId}-${row.DateInfo}`)
                                                );
                                                setNotDocumentedSelectedRows(selected || []);
                                            }}
                                            getRowId={(row) => `${row.ItemId}-${row.DateInfo}`}
                                            paginationMode="server"
                                            hideFooterPagination={true}
                                            hideFooter={true}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </Paper>
        </LocalizationProvider>
    );
};

export default DocumentationTrackingSummaryDetail;
